export interface FractionRepresentations {
  numerator: number;
  denominator: number;
  decimal: number;
  percent: number;
  ratio: string;
  simplified: string;
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b !== 0) [a, b] = [b, a % b];
  return a || 1;
}

export function fractionRepresentations(
  numerator: number,
  denominator: number,
): FractionRepresentations {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator <= 0) {
    throw new Error('A fraction needs an integer numerator and a positive integer denominator.');
  }
  const divisor = greatestCommonDivisor(numerator, denominator);
  return {
    numerator,
    denominator,
    decimal: numerator / denominator,
    percent: (numerator / denominator) * 100,
    ratio: `${numerator}:${Math.max(0, denominator - numerator)}`,
    simplified: `${numerator / divisor}/${denominator / divisor}`,
  };
}

export interface EquationStep {
  equation: string;
  reason: string;
}

export function solveLinearEquation(a: number, b: number, c: number): {
  solution: number | null;
  steps: EquationStep[];
} {
  if (![a, b, c].every(Number.isFinite)) throw new Error('Equation values must be finite.');
  if (a === 0) return { solution: null, steps: [] };
  const afterSubtract = c - b;
  const solution = afterSubtract / a;
  return {
    solution,
    steps: [
      {
        equation: `${a}x = ${afterSubtract}`,
        reason: `${b >= 0 ? 'Subtract' : 'Add'} ${Math.abs(b)} on both sides.`,
      },
      {
        equation: `x = ${formatMathNumber(solution)}`,
        reason: `Divide both sides by ${a}.`,
      },
    ],
  };
}

export interface UniqueSimultaneousSolution {
  kind: 'unique';
  determinant: number;
  x: number;
  y: number;
  basisEquationIndices?: [number, number];
}

export interface NoSimultaneousSolution {
  kind: 'none';
  determinant: 0;
  reason: 'parallel' | 'contradiction' | 'inconsistent';
  basisEquationIndices?: [number, number];
  conflictingEquationIndices?: number[];
  candidateIntersection?: { x: number; y: number };
}

export interface InfiniteSimultaneousSolution {
  kind: 'infinite';
  determinant: 0;
  reason: 'coincident' | 'identity' | 'all-points';
}

export type SimultaneousSolution = UniqueSimultaneousSolution | NoSimultaneousSolution | InfiniteSimultaneousSolution;

export interface LinearEquation {
  x: number;
  y: number;
  result: number;
}

const LINEAR_SYSTEM_EPSILON = 1e-10;

export function solveLinearSystem(equations: LinearEquation[]): SimultaneousSolution {
  if (equations.length < 2) throw new Error('A linear system needs at least two equations.');
  const values = equations.flatMap((equation) => [equation.x, equation.y, equation.result]);
  if (!values.every(Number.isFinite)) throw new Error('Equation coefficients must be finite.');
  const isZero = (value: number) => Math.abs(value) < LINEAR_SYSTEM_EPSILON;
  const contradiction = equations.some((equation) => isZero(equation.x) && isZero(equation.y) && !isZero(equation.result));
  if (contradiction) return { kind: 'none', determinant: 0, reason: 'contradiction' };

  const active = equations
    .map((equation, index) => ({ equation, index }))
    .filter(({ equation }) => !isZero(equation.x) || !isZero(equation.y));
  if (active.length === 0) return { kind: 'infinite', determinant: 0, reason: 'all-points' };
  if (active.length === 1) return { kind: 'infinite', determinant: 0, reason: 'identity' };

  for (let firstIndex = 0; firstIndex < active.length - 1; firstIndex += 1) {
    for (let secondIndex = firstIndex + 1; secondIndex < active.length; secondIndex += 1) {
      const first = active[firstIndex];
      const second = active[secondIndex];
      const determinant = first.equation.x * second.equation.y - first.equation.y * second.equation.x;
      if (isZero(determinant)) continue;
      const x = (first.equation.result * second.equation.y - first.equation.y * second.equation.result) / determinant;
      const y = (first.equation.x * second.equation.result - first.equation.result * second.equation.x) / determinant;
      const sharedByAll = active.every(({ equation }) => (
        Math.abs(equation.x * x + equation.y * y - equation.result) < LINEAR_SYSTEM_EPSILON
      ));
      const conflictingEquationIndices = active
        .filter(({ equation }) => Math.abs(equation.x * x + equation.y * y - equation.result) >= LINEAR_SYSTEM_EPSILON)
        .map(({ index }) => index);
      return sharedByAll
        ? { kind: 'unique', determinant, x, y, basisEquationIndices: [first.index, second.index] }
        : {
          kind: 'none',
          determinant: 0,
          reason: 'inconsistent',
          basisEquationIndices: [first.index, second.index],
          conflictingEquationIndices,
          candidateIntersection: { x, y },
        };
    }
  }

  const reference = active[0].equation;
  const allCoincident = active.every(({ equation }) => (
    isZero(reference.x * equation.result - reference.result * equation.x)
    && isZero(reference.y * equation.result - reference.result * equation.y)
  ));
  return allCoincident
    ? { kind: 'infinite', determinant: 0, reason: 'coincident' }
    : { kind: 'none', determinant: 0, reason: 'parallel' };
}

export type LinearSystemMethod = 'elimination' | 'substitution';

export interface LinearSystemWorking {
  method: LinearSystemMethod;
  variable: 'x' | 'y';
  basisEquationIndices: [number, number];
  steps: Array<{ title: string; equation: string; explanation: string }>;
}

export function linearSystemWorking(
  equations: LinearEquation[],
  method: LinearSystemMethod,
  preferredVariable: 'x' | 'y',
): LinearSystemWorking | null {
  const solution = solveLinearSystem(equations);
  if (solution.kind !== 'unique') return null;
  const basis = solution.basisEquationIndices ?? [0, 1];
  const first = equations[basis[0]];
  const second = equations[basis[1]];
  if (method === 'substitution') return substitutionWorking(first, second, basis, solution, preferredVariable);
  return eliminationWorking(first, second, basis, solution, preferredVariable);
}

function eliminationWorking(
  first: LinearEquation,
  second: LinearEquation,
  basis: [number, number],
  solution: UniqueSimultaneousSolution,
  preferredVariable: 'x' | 'y',
): LinearSystemWorking {
  const variable = preferredVariable;
  const firstVariableCoefficient = variable === 'x' ? first.x : first.y;
  const secondVariableCoefficient = variable === 'x' ? second.x : second.y;
  if (firstVariableCoefficient === 0 || secondVariableCoefficient === 0) {
    const directEquation = firstVariableCoefficient === 0 ? first : second;
    const directIndex = firstVariableCoefficient === 0 ? basis[0] : basis[1];
    const remainingVariable = variable === 'x' ? 'y' : 'x';
    const remainingValue = remainingVariable === 'x' ? solution.x : solution.y;
    const eliminatedValue = variable === 'x' ? solution.x : solution.y;
    return {
      method: 'elimination',
      variable,
      basisEquationIndices: basis,
      steps: [
        {
          title: `${variable} is already eliminated`,
          equation: `E${directIndex + 1}: ${formatLinearEquation(directEquation)}`,
          explanation: `Equation ${directIndex + 1} already contains only ${remainingVariable}, so no scaling is needed.`,
        },
        {
          title: `Solve for ${remainingVariable}`,
          equation: `${remainingVariable} = ${formatMathNumber(remainingValue)}`,
          explanation: 'Divide by its coefficient.',
        },
        {
          title: 'Substitute back',
          equation: `${variable} = ${formatMathNumber(eliminatedValue)}`,
          explanation: `Put ${remainingVariable} = ${formatMathNumber(remainingValue)} into the other equation.`,
        },
      ],
    };
  }
  const firstMultiplier = variable === 'x' ? second.x : second.y;
  const secondMultiplier = variable === 'x' ? first.x : first.y;
  const scaledFirst = scaleEquation(first, firstMultiplier);
  const scaledSecond = scaleEquation(second, secondMultiplier);
  const remainingVariable = variable === 'x' ? 'y' : 'x';
  const remainingCoefficient = variable === 'x'
    ? scaledFirst.y - scaledSecond.y
    : scaledFirst.x - scaledSecond.x;
  const remainingResult = scaledFirst.result - scaledSecond.result;
  const remainingValue = remainingVariable === 'x' ? solution.x : solution.y;
  const eliminatedValue = variable === 'x' ? solution.x : solution.y;
  return {
    method: 'elimination',
    variable,
    basisEquationIndices: basis,
    steps: [
      {
        title: `Match the ${variable}-coefficients`,
        equation: `E${basis[0] + 1} × ${formatMathNumber(firstMultiplier)}: ${formatLinearEquation(scaledFirst)}`,
        explanation: `Scale Equation ${basis[0] + 1} so its ${variable}-coefficient matches Equation ${basis[1] + 1}.`,
      },
      {
        title: 'Scale the other equation',
        equation: `E${basis[1] + 1} × ${formatMathNumber(secondMultiplier)}: ${formatLinearEquation(scaledSecond)}`,
        explanation: `The two ${variable}-terms are now equal, so subtracting will remove them.`,
      },
      {
        title: `Eliminate ${variable}`,
        equation: `${formatMathNumber(remainingCoefficient)}${remainingVariable} = ${formatMathNumber(remainingResult)}`,
        explanation: `Subtract the scaled second equation from the scaled first equation.`,
      },
      {
        title: `Solve for ${remainingVariable}`,
        equation: `${remainingVariable} = ${formatMathNumber(remainingValue)}`,
        explanation: `Divide by ${formatMathNumber(remainingCoefficient)}.`,
      },
      {
        title: 'Substitute back',
        equation: `${variable} = ${formatMathNumber(eliminatedValue)}`,
        explanation: `Put ${remainingVariable} = ${formatMathNumber(remainingValue)} into Equation ${basis[0] + 1}.`,
      },
    ],
  };
}

function substitutionWorking(
  first: LinearEquation,
  second: LinearEquation,
  basis: [number, number],
  solution: UniqueSimultaneousSolution,
  preferredVariable: 'x' | 'y',
): LinearSystemWorking {
  const candidates = [
    { equation: first, index: basis[0] },
    { equation: second, index: basis[1] },
  ];
  let variable = preferredVariable;
  let usable = candidates.filter(({ equation }) => (variable === 'x' ? equation.x : equation.y) !== 0);
  if (usable.length === 0) {
    variable = variable === 'x' ? 'y' : 'x';
    usable = candidates.filter(({ equation }) => (variable === 'x' ? equation.x : equation.y) !== 0);
  }
  const source = usable.reduce((best, item) => (
    Math.abs(variable === 'x' ? item.equation.x : item.equation.y)
      < Math.abs(variable === 'x' ? best.equation.x : best.equation.y) ? item : best
  ));
  const target = source.index === basis[0] ? candidates[1] : candidates[0];
  const coefficient = variable === 'x' ? source.equation.x : source.equation.y;
  const otherVariable = variable === 'x' ? 'y' : 'x';
  const otherCoefficient = variable === 'x' ? source.equation.y : source.equation.x;
  const numerator = `${formatAlgebraNumber(source.equation.result)} ${otherCoefficient < 0 ? '+' : '−'} ${formatVariableMagnitude(otherCoefficient, otherVariable)}`;
  const expression = coefficient === 1 ? numerator : `(${numerator}) ÷ ${formatAlgebraNumber(coefficient)}`;
  const targetVariableCoefficient = variable === 'x' ? target.equation.x : target.equation.y;
  const targetOtherCoefficient = variable === 'x' ? target.equation.y : target.equation.x;
  const variableValue = variable === 'x' ? solution.x : solution.y;
  const otherValue = otherVariable === 'x' ? solution.x : solution.y;
  return {
    method: 'substitution',
    variable,
    basisEquationIndices: basis,
    steps: [
      {
        title: `Isolate ${variable}`,
        equation: `${variable} = ${expression}`,
        explanation: `Rearrange Equation ${source.index + 1}; it has the simplest available ${variable}-coefficient.`,
      },
      {
        title: `Substitute into Equation ${target.index + 1}`,
        equation: `${formatExpressionFactor(targetVariableCoefficient, expression)} ${targetOtherCoefficient < 0 ? '−' : '+'} ${formatVariableMagnitude(targetOtherCoefficient, otherVariable)} = ${formatAlgebraNumber(target.equation.result)}`,
        explanation: `Replace ${variable} in Equation ${target.index + 1} with the equivalent expression.`,
      },
      {
        title: `Solve for ${otherVariable}`,
        equation: `${otherVariable} = ${formatMathNumber(otherValue)}`,
        explanation: `Now the equation contains only one unknown.`,
      },
      {
        title: 'Substitute back',
        equation: `${variable} = ${formatMathNumber(variableValue)}`,
        explanation: `Use ${otherVariable} = ${formatMathNumber(otherValue)} in the isolated expression.`,
      },
    ],
  };
}

function scaleEquation(equation: LinearEquation, multiplier: number): LinearEquation {
  return {
    x: equation.x * multiplier,
    y: equation.y * multiplier,
    result: equation.result * multiplier,
  };
}

function formatLinearEquation(equation: LinearEquation) {
  return `${formatAlgebraNumber(equation.x)}x ${equation.y < 0 ? '−' : '+'} ${formatVariableMagnitude(equation.y, 'y')} = ${formatAlgebraNumber(equation.result)}`;
}

function formatAlgebraNumber(value: number) {
  return formatMathNumber(value).replace(/^-/, '−');
}

function formatVariableMagnitude(coefficient: number, variable: 'x' | 'y') {
  const magnitude = Math.abs(coefficient);
  return `${magnitude === 1 ? '' : formatMathNumber(magnitude)}${variable}`;
}

function formatExpressionFactor(coefficient: number, expression: string) {
  const magnitude = Math.abs(coefficient);
  const prefix = coefficient < 0 ? '−' : '';
  return `${prefix}${magnitude === 1 ? '' : formatMathNumber(magnitude)}(${expression})`;
}

export function solveSimultaneousEquations(
  firstX: number,
  firstY: number,
  firstResult: number,
  secondX: number,
  secondY: number,
  secondResult: number,
): SimultaneousSolution {
  const solution = solveLinearSystem([
    { x: firstX, y: firstY, result: firstResult },
    { x: secondX, y: secondY, result: secondResult },
  ]);
  if (solution.kind !== 'unique') return solution;
  return {
    kind: 'unique',
    determinant: solution.determinant,
    x: solution.x,
    y: solution.y,
  };
}

export type FunctionKind = 'linear' | 'quadratic' | 'exponential';

export function functionValue(kind: FunctionKind, a: number, b: number, x: number): number {
  if (kind === 'linear') return a * x + b;
  if (kind === 'quadratic') return a * x * x + b;
  return a * Math.exp(b * x);
}

export function functionTable(kind: FunctionKind, a: number, b: number) {
  return [-2, -1, 0, 1, 2].map((x) => ({ x, y: functionValue(kind, a, b, x) }));
}

export interface QuadraticSolution {
  discriminant: number;
  roots: number[];
}

export function solveQuadratic(a: number, b: number, c: number): QuadraticSolution {
  if (![a, b, c].every(Number.isFinite)) throw new Error('Quadratic coefficients must be finite.');
  if (a === 0) throw new Error('A quadratic equation needs a non-zero x-squared coefficient.');
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return { discriminant, roots: [] };
  if (discriminant === 0) return { discriminant, roots: [-b / (2 * a)] };
  const squareRoot = Math.sqrt(discriminant);
  return {
    discriminant,
    roots: [(-b - squareRoot) / (2 * a), (-b + squareRoot) / (2 * a)].sort((left, right) => left - right),
  };
}

export type GeometryShape =
  | 'rectangle'
  | 'triangle'
  | 'circle'
  | 'prism'
  | 'cylinder'
  | 'cone'
  | 'sphere';

export function geometryMeasures(shape: GeometryShape, first: number, second: number) {
  if (first <= 0 || second <= 0) throw new Error('Measurements must be positive.');
  if (shape === 'rectangle') {
    return { perimeter: 2 * (first + second), area: first * second, volume: null };
  }
  if (shape === 'triangle') {
    return { perimeter: null, area: (first * second) / 2, volume: null };
  }
  if (shape === 'circle') {
    return { perimeter: 2 * Math.PI * first, area: Math.PI * first * first, volume: null };
  }
  if (shape === 'prism') {
    return {
      perimeter: null,
      area: 2 * (first * second + first * first + second * first),
      volume: first * first * second,
    };
  }
  if (shape === 'cylinder') {
    return {
      perimeter: null,
      area: 2 * Math.PI * first * (first + second),
      volume: Math.PI * first * first * second,
    };
  }
  if (shape === 'cone') {
    const slantHeight = Math.sqrt(first * first + second * second);
    return {
      perimeter: null,
      area: Math.PI * first * (first + slantHeight),
      volume: (Math.PI * first * first * second) / 3,
    };
  }
  return {
    perimeter: null,
    area: 4 * Math.PI * first * first,
    volume: (4 * Math.PI * first * first * first) / 3,
  };
}

export type CoordinateTransform = 'translate' | 'reflect-x' | 'reflect-y' | 'rotate-90' | 'scale';

export interface Point2D {
  x: number;
  y: number;
}

export function transformPoint(
  point: Point2D,
  transform: CoordinateTransform,
  options: { horizontal?: number; vertical?: number; factor?: number } = {},
): Point2D {
  if (![point.x, point.y].every(Number.isFinite)) throw new Error('Point coordinates must be finite.');
  if (transform === 'translate') {
    return {
      x: point.x + (options.horizontal ?? 0),
      y: point.y + (options.vertical ?? 0),
    };
  }
  if (transform === 'reflect-x') return { x: point.x, y: -point.y };
  if (transform === 'reflect-y') return { x: -point.x, y: point.y };
  if (transform === 'rotate-90') return { x: -point.y, y: point.x };
  const factor = options.factor ?? 1;
  if (!Number.isFinite(factor) || factor === 0) throw new Error('Scale factor must be finite and non-zero.');
  return { x: point.x * factor, y: point.y * factor };
}

export function distanceBetweenPoints(first: Point2D, second: Point2D): number {
  if (![first.x, first.y, second.x, second.y].every(Number.isFinite)) {
    throw new Error('Point coordinates must be finite.');
  }
  return Math.hypot(second.x - first.x, second.y - first.y);
}

export function addVectors(first: Point2D, second: Point2D): Point2D {
  if (![first.x, first.y, second.x, second.y].every(Number.isFinite)) {
    throw new Error('Vector components must be finite.');
  }
  return { x: first.x + second.x, y: first.y + second.y };
}

export function vectorMagnitude(vector: Point2D): number {
  if (![vector.x, vector.y].every(Number.isFinite)) throw new Error('Vector components must be finite.');
  return Math.hypot(vector.x, vector.y);
}

export type TriangleQuestion = 'hypotenuse' | 'opposite' | 'angle';

export function rightTriangleValue(question: TriangleQuestion, adjacent: number, opposite: number): number {
  if (adjacent <= 0 || opposite <= 0) throw new Error('Triangle sides must be positive.');
  if (question === 'hypotenuse') return Math.hypot(adjacent, opposite);
  if (question === 'opposite') return opposite;
  return (Math.atan2(opposite, adjacent) * 180) / Math.PI;
}

export function cosineRuleSide(firstSide: number, secondSide: number, includedAngleDegrees: number): number {
  if (![firstSide, secondSide, includedAngleDegrees].every(Number.isFinite)) {
    throw new Error('Triangle values must be finite.');
  }
  if (firstSide <= 0 || secondSide <= 0) throw new Error('Triangle sides must be positive.');
  if (includedAngleDegrees <= 0 || includedAngleDegrees >= 180) {
    throw new Error('The included angle must be between zero and 180 degrees.');
  }
  const angleRadians = (includedAngleDegrees * Math.PI) / 180;
  return Math.sqrt(
    firstSide ** 2 + secondSide ** 2 - 2 * firstSide * secondSide * Math.cos(angleRadians),
  );
}

export type SequenceKind = 'arithmetic' | 'geometric';

export function sequenceTerms(kind: SequenceKind, first: number, change: number, count = 8): number[] {
  const safeCount = Math.max(1, Math.min(20, Math.floor(count)));
  return Array.from({ length: safeCount }, (_, index) => (
    kind === 'arithmetic' ? first + index * change : first * change ** index
  ));
}

export function sequenceTerm(kind: SequenceKind, first: number, change: number, position: number): number {
  if (!Number.isInteger(position) || position < 1 || ![first, change].every(Number.isFinite))
    throw new Error('A sequence needs finite values and a positive whole-number position.');
  const n = position;
  return kind === 'arithmetic' ? first + (n - 1) * change : first * change ** (n - 1);
}

export function logarithmValue(base: number, value: number): number {
  if (![base, value].every(Number.isFinite)) throw new Error('Logarithm values must be finite.');
  if (base <= 0 || base === 1) throw new Error('A logarithm base must be positive and not equal to one.');
  if (value <= 0) throw new Error('A logarithm input must be positive.');
  return Math.log(value) / Math.log(base);
}

export type MoneyModel = 'percentage' | 'simple-interest' | 'compound-interest';

export function moneyOutcome(model: MoneyModel, principal: number, ratePercent: number, periods: number) {
  if (principal < 0 || periods < 0) throw new Error('Money and time cannot be negative.');
  const rate = ratePercent / 100;
  if (model === 'percentage') {
    const change = principal * rate;
    return { change, final: principal + change };
  }
  if (model === 'simple-interest') {
    const change = principal * rate * periods;
    return { change, final: principal + change };
  }
  const final = principal * (1 + rate) ** periods;
  return { change: final - principal, final };
}

export interface LoanRepayment {
  monthlyPayment: number;
  totalPaid: number;
  totalInterest: number;
}

export function loanRepayment(principal: number, annualRatePercent: number, years: number): LoanRepayment {
  if (![principal, annualRatePercent, years].every(Number.isFinite)) {
    throw new Error('Loan values must be finite.');
  }
  if (principal <= 0 || annualRatePercent < 0 || years <= 0) {
    throw new Error('A loan needs a positive principal and term, and a non-negative rate.');
  }
  const payments = Math.max(1, Math.round(years * 12));
  const monthlyRate = annualRatePercent / 1200;
  const monthlyPayment = monthlyRate === 0
    ? principal / payments
    : (principal * monthlyRate * (1 + monthlyRate) ** payments)
      / ((1 + monthlyRate) ** payments - 1);
  const totalPaid = monthlyPayment * payments;
  return { monthlyPayment, totalPaid, totalInterest: totalPaid - principal };
}

export function powerRate(coefficient: number, exponent: number, x: number) {
  return {
    value: coefficient * x ** exponent,
    gradient: coefficient * exponent * x ** (exponent - 1),
  };
}

export function quadraticTurningPoint(a: number, b: number, c: number) {
  if (![a, b, c].every(Number.isFinite)) throw new Error('Quadratic values must be finite.');
  if (a === 0) throw new Error('A quadratic needs a non-zero leading coefficient.');
  const x = -b / (2 * a);
  return { x, y: a * x ** 2 + b * x + c, kind: a > 0 ? 'minimum' as const : 'maximum' as const };
}

export type IndexOperation = 'multiply' | 'divide' | 'power';

export function indexLawExponent(operation: IndexOperation, firstExponent: number, secondExponent: number): number {
  if (operation === 'multiply') return firstExponent + secondExponent;
  if (operation === 'divide') return firstExponent - secondExponent;
  return firstExponent * secondExponent;
}

export function powerAccumulation(coefficient: number, exponent: number, from: number, to: number): number {
  if (exponent === -1) throw new Error('The power-rule integral does not apply when the exponent is -1.');
  const antiderivative = (value: number) => (coefficient * value ** (exponent + 1)) / (exponent + 1);
  return antiderivative(to) - antiderivative(from);
}

export interface DataSummary {
  sorted: number[];
  mean: number;
  median: number;
  range: number;
  minimum: number;
  maximum: number;
}

export function parseDataValues(value: string): number[] {
  return value
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number)
    .filter(Number.isFinite)
    .slice(0, 30);
}

export function summariseData(values: readonly number[]): DataSummary | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
  return {
    sorted,
    mean: sorted.reduce((sum, item) => sum + item, 0) / sorted.length,
    median,
    range: sorted[sorted.length - 1] - sorted[0],
    minimum: sorted[0],
    maximum: sorted[sorted.length - 1],
  };
}

export function standardScore(value: number, mean: number, standardDeviation: number): number {
  if (![value, mean, standardDeviation].every(Number.isFinite)) {
    throw new Error('Standard-score values must be finite.');
  }
  if (standardDeviation <= 0) throw new Error('Standard deviation must be positive.');
  return (value - mean) / standardDeviation;
}

export interface LinearRegressionSummary {
  slope: number;
  intercept: number;
  correlation: number;
}

export function linearRegression(points: readonly Point2D[]): LinearRegressionSummary {
  if (points.length < 2) throw new Error('Regression needs at least two points.');
  if (!points.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) {
    throw new Error('Regression points must be finite.');
  }
  const meanX = points.reduce((sum, point) => sum + point.x, 0) / points.length;
  const meanY = points.reduce((sum, point) => sum + point.y, 0) / points.length;
  const covariance = points.reduce((sum, point) => sum + (point.x - meanX) * (point.y - meanY), 0);
  const varianceX = points.reduce((sum, point) => sum + (point.x - meanX) ** 2, 0);
  const varianceY = points.reduce((sum, point) => sum + (point.y - meanY) ** 2, 0);
  if (varianceX === 0) throw new Error('Regression needs more than one x value.');
  const slope = covariance / varianceX;
  const intercept = meanY - slope * meanX;
  const correlation = varianceY === 0 ? 0 : covariance / Math.sqrt(varianceX * varianceY);
  return { slope, intercept, correlation };
}

function errorFunction(value: number): number {
  const sign = value < 0 ? -1 : 1;
  const x = Math.abs(value);
  const t = 1 / (1 + 0.3275911 * x);
  const polynomial = (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t;
  return sign * (1 - polynomial * Math.exp(-x * x));
}

export function normalCumulativeProbability(value: number, mean: number, standardDeviation: number): number {
  const z = standardScore(value, mean, standardDeviation);
  return 0.5 * (1 + errorFunction(z / Math.SQRT2));
}

function seededUnit(seed: number): number {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
}

export function simulateCoinFlips(trials: number, seed: number): { heads: number; tails: number } {
  const safeTrials = Math.max(1, Math.min(10000, Math.floor(trials)));
  let heads = 0;
  for (let index = 0; index < safeTrials; index += 1) {
    if (seededUnit(seed + index) < 0.5) heads += 1;
  }
  return { heads, tails: safeTrials - heads };
}

export function formatMathNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}
